import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, User as UserIcon, LogIn, AlertCircle } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { LogoLockup } from '../components/Logo';

export default function Login() {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) navigate('/', { replace: true });
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username || !password) {
      setError('Please enter username and password');
      return;
    }
    setLoading(true);
    setError('');
    const ok = await login(username, password);
    if (ok) {
      navigate('/', { replace: true });
    } else {
      setError('Invalid username or password');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left hero (desktop only) */}
      <div
        className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden bg-brand-800"
        style={{
          backgroundImage:
            'linear-gradient(135deg, rgba(10,59,122,0.92), rgba(4,26,55,0.95)), url("https://images.unsplash.com/photo-1676210134188-4c05dd172f89?w=1200&q=80")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="bg-white/95 rounded-2xl px-5 py-4 backdrop-blur inline-flex items-center self-start shadow-lg">
          <LogoLockup size={56} color="dark" />
        </div>

        <div className="max-w-md">
          <h1 className="text-white text-4xl font-bold leading-tight mb-4">
            Manage every job, from booking to invoice.
          </h1>
          <p className="text-white/80 text-lg leading-relaxed">
            Track schedules, parts orders, and Xero-ready invoices — all in one place.
          </p>
        </div>

        <div className="text-white/60 text-sm">
          © {new Date().getFullYear()} A. Forsyth & Son
        </div>
      </div>

      {/* Right form */}
      <div className="flex flex-col justify-center items-center px-6 py-10 bg-white">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex justify-center mb-8">
            <LogoLockup size={52} color="dark" />
          </div>

          <h2 className="text-3xl font-bold text-slate-900 mb-1">Welcome back</h2>
          <p className="text-slate-500 mb-8">Sign in to manage your jobs</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm font-medium">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {error}
              </div>
            )}

            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 block">
                Username
              </label>
              <div className="relative">
                <UserIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  autoComplete="username"
                  className="input pl-10 py-3"
                  placeholder="Enter your username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={loading}
                  data-testid="login-username-input"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 block">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type={showPw ? 'text' : 'password'}
                  autoComplete="current-password"
                  className="input pl-10 pr-10 py-3"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  tabIndex={-1}
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary btn-lg w-full mt-2"
              data-testid="login-form-submit-button"
            >
              {loading ? (
                <>
                  <div className="animate-spin h-4 w-4 rounded-full border-2 border-white border-t-transparent" />
                  Signing in…
                </>
              ) : (
                <>
                  <LogIn className="h-4 w-4" />
                  Sign In
                </>
              )}
            </button>
          </form>

          <div className="mt-8 text-xs text-center text-slate-400">
            © A. Forsyth &amp; Son · Plumbing &amp; Groundworks
          </div>
        </div>
      </div>
    </div>
  );
}
